/**
 * LeaderReviewPeers.tsx
 * Wizard-style peer review — low cognitive load, one step at a time.
 *
 * Flow:
 *   1. Peer List  — cards showing each peer + status, select to begin
 *   2. Muscle Wizard — ONE muscle per screen, step indicator, score selector
 *   3. Wrap-up     — score summary + strength/growth → Submit
 */

import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  fetchSession, fetchEnrollments, fetchMyPeerRatings,
  submitPeerRating, resolveSessionMuscles,
} from '../services/leaderReviewService';
import { LEADER_MUSCLES } from '../types';
import type {
  LeaderReviewSession, LeaderReviewEnrollment,
  LeaderMuscleScore, LeaderMuscle,
} from '../types';

// ─── Score labels & colours ───────────────────────────────────────────────────

const SCORE_LABELS: Record<number, string> = {
  1: 'Weak',
  2: 'Inconsistent',
  3: 'Solid',
  4: 'Strong',
  5: 'Leading',
};

const SCORE_COLORS: Record<number, { bg: string; border: string; text: string }> = {
  1: { bg: 'bg-red-50',    border: 'border-red-300',    text: 'text-red-700'    },
  2: { bg: 'bg-orange-50', border: 'border-orange-300', text: 'text-orange-700' },
  3: { bg: 'bg-amber-50',  border: 'border-amber-300',  text: 'text-amber-700'  },
  4: { bg: 'bg-blue-50',   border: 'border-blue-300',   text: 'text-blue-700'   },
  5: { bg: 'bg-emerald-50',border: 'border-emerald-400',text: 'text-emerald-700'},
};

// ─── Avatar circle ────────────────────────────────────────────────────────────

function Avatar({ name, size = 'md' }: { name: string; size?: 'sm' | 'md' | 'lg' }) {
  const initials = name.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase();
  const cls = size === 'sm' ? 'w-8 h-8 text-xs' : size === 'lg' ? 'w-14 h-14 text-lg' : 'w-10 h-10 text-sm';
  return (
    <div className={`${cls} rounded-full bg-[#011132]/10 flex items-center justify-center flex-shrink-0 font-bold text-[#011132]`}>
      {initials}
    </div>
  );
}

// ─── KR bar ───────────────────────────────────────────────────────────────────

function KRBar({ pct }: { pct: number }) {
  const bar = pct >= 80 ? 'bg-emerald-500' : pct >= 50 ? 'bg-amber-400' : 'bg-red-400';
  const txt = pct >= 80 ? 'text-emerald-700' : pct >= 50 ? 'text-amber-700' : 'text-red-600';
  return (
    <div className="flex items-center gap-2 mt-1.5">
      <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden">
        <div className={`h-full rounded-full ${bar}`} style={{ width: `${pct}%` }} />
      </div>
      <span className={`text-xs font-bold ${txt} w-8 text-right`}>{pct}%</span>
    </div>
  );
}

// ─── Main ─────────────────────────────────────────────────────────────────────

type View = 'list' | 'review' | 'wrapup' | 'done';

const LeaderReviewPeers: React.FC = () => {
  const { sessionId } = useParams<{ sessionId: string }>();
  const { user }      = useAuth();
  const navigate      = useNavigate();

  const [session, setSession]           = useState<LeaderReviewSession | null>(null);
  const [muscles, setMuscles]           = useState<LeaderMuscle[]>(LEADER_MUSCLES);
  const [peers, setPeers]               = useState<LeaderReviewEnrollment[]>([]);
  const [alreadyRated, setAlreadyRated] = useState<Set<string>>(new Set());
  const [loading, setLoading]           = useState(true);
  const [saving, setSaving]             = useState(false);

  // Wizard navigation
  const [view, setView]                       = useState<View>('list');
  const [currentPeerIdx, setCurrentPeerIdx]   = useState(0);
  const [currentMuscleIdx, setCurrentMuscleIdx] = useState(0);
  const [showAnchor, setShowAnchor]           = useState(false);

  // Per-peer rating state
  const emptyScores = (ms: LeaderMuscle[]) => ms.map(m => ({ muscleKey: m.key, score: 0 }));
  const [scores, setScores]         = useState<LeaderMuscleScore[]>([]);
  const [strength, setStrength]     = useState('');
  const [growthArea, setGrowthArea] = useState('');

  const load = useCallback(async () => {
    if (!sessionId || !user?.uid) return;
    setLoading(true);
    try {
      const [sess, enrollments, myRatings] = await Promise.all([
        fetchSession(sessionId),
        fetchEnrollments(sessionId),
        fetchMyPeerRatings(sessionId, user.uid),
      ]);
      setSession(sess);
      const resolvedMuscles = sess ? await resolveSessionMuscles(sess) : LEADER_MUSCLES;
      setMuscles(resolvedMuscles);
      setScores(emptyScores(resolvedMuscles));
      const filteredPeers = enrollments.filter(e => e.leaderId !== user.uid);
      setPeers(filteredPeers);
      setAlreadyRated(new Set(myRatings.map(r => r.ratedLeaderId)));
    } finally {
      setLoading(false);
    }
  }, [sessionId, user?.uid]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load]);

  // ── Wizard actions ───────────────────────────────────────────────────────────

  const startReview = (peerIdx: number) => {
    setCurrentPeerIdx(peerIdx);
    setCurrentMuscleIdx(0);
    setShowAnchor(false);
    setScores(emptyScores(muscles));
    setStrength('');
    setGrowthArea('');
    setView('review');
  };

  const handlePrevMuscle = () => {
    if (currentMuscleIdx > 0) {
      setCurrentMuscleIdx(i => i - 1);
      setShowAnchor(false);
    }
  };

  const handleNextMuscle = () => {
    setShowAnchor(false);
    if (currentMuscleIdx < muscles.length - 1) {
      setCurrentMuscleIdx(i => i + 1);
    } else {
      setView('wrapup');
    }
  };

  const setScore = (muscleKey: string, val: number) => {
    setScores(prev => prev.map(s => s.muscleKey === muscleKey ? { ...s, score: val } : s));
  };

  const handleSubmit = async () => {
    if (!sessionId || !user?.uid || saving) return;
    const peer = peers[currentPeerIdx];
    if (!peer) return;
    setSaving(true);
    try {
      await submitPeerRating(sessionId, user.uid, peer.leaderId, scores, strength, growthArea);
      const newRated = new Set([...alreadyRated, peer.leaderId]);
      setAlreadyRated(newRated);
      // All peers done → completion screen; else back to list
      if (newRated.size >= totalPeers && totalPeers > 0) {
        setView('done');
      } else {
        setView('list');
      }
    } finally {
      setSaving(false);
    }
  };

  // ── Derived ──────────────────────────────────────────────────────────────────

  const currentPeer    = peers[currentPeerIdx];
  const currentMuscle  = muscles[currentMuscleIdx];
  const currentScore   = scores.find(s => s.muscleKey === currentMuscle?.key)?.score ?? 0;
  const allScored      = scores.every(s => s.score > 0);
  const ratedCount     = alreadyRated.size;
  const totalPeers     = peers.length;
  const progressPct    = totalPeers ? Math.round((ratedCount / totalPeers) * 100) : 0;
  const allDone        = ratedCount >= totalPeers && totalPeers > 0;

  // ── Loading ──────────────────────────────────────────────────────────────────

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <div className="w-5 h-5 border-2 border-[#011132] border-t-transparent rounded-full animate-spin" />
    </div>
  );

  // ── Shared header ────────────────────────────────────────────────────────────

  const PageHeader = ({ onBack, title, subtitle }: { onBack: () => void; title: string; subtitle?: string }) => (
    <div className="flex items-center gap-3 mb-6">
      <button onClick={onBack} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 text-gray-500 transition-colors flex-shrink-0">
        <span className="material-symbols-rounded" style={{ fontSize: 20 }}>arrow_back</span>
      </button>
      <div className="flex-1 min-w-0">
        <h1 className="text-lg font-bold text-gray-900">{title}</h1>
        {subtitle && <p className="text-sm text-gray-600 truncate">{subtitle}</p>}
      </div>
    </div>
  );

  // ════════════════════════════════════════════════════════════════════════════
  // VIEW 4 — Completion (all peers rated — checked before list to intercept)
  // ════════════════════════════════════════════════════════════════════════════

  if (view === 'done' || (view === 'list' && allDone)) return (
    <div className="max-w-2xl mx-auto px-4 py-10">

      {/* Success header */}
      <div className="text-center mb-7">
        <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-3">
          <span className="material-symbols-rounded text-emerald-600" style={{ fontSize: 32 }}>diversity_3</span>
        </div>
        <h2 className="text-xl font-bold text-gray-900 mb-1">Peer Reviews Complete!</h2>
        <p className="text-sm text-gray-600">
          You've rated all {totalPeers} peer{totalPeers !== 1 ? 's' : ''} — your feedback will be aggregated confidentially.
        </p>
      </div>

      {/* Summary card */}
      <div className="bg-white border border-gray-200 rounded-2xl p-5 mb-4">
        <p className="text-xs font-bold text-gray-600 uppercase tracking-wider mb-3">What you submitted</p>
        <div className="space-y-2">
          {peers.map(peer => (
            <div key={peer.id} className="flex items-center gap-3">
              <Avatar name={peer.leaderName} size="sm" />
              <p className="flex-1 text-sm font-medium text-gray-800 truncate">{peer.leaderName}</p>
              <span className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-700 bg-emerald-100 px-2 py-0.5 rounded-full flex-shrink-0">
                <span className="material-symbols-rounded" style={{ fontSize: 12 }}>check</span>
                Rated
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* What's next */}
      {session?.liveSessionDate ? (
        <div className="bg-purple-50 border border-purple-200 rounded-2xl p-4 mb-4">
          <div className="flex items-center gap-2 mb-1">
            <span className="material-symbols-rounded text-purple-600" style={{ fontSize: 18 }}>video_call</span>
            <p className="text-sm font-bold text-purple-800">Next: Live Session</p>
          </div>
          <p className="text-xs text-purple-700 leading-relaxed">
            The live calibration session is scheduled for <span className="font-semibold">{session.liveSessionDate}</span>. You'll receive feedback on your leadership muscles from peers and your facilitator.
          </p>
        </div>
      ) : (
        <div className="bg-gray-50 border border-gray-200 rounded-2xl p-4 mb-4">
          <div className="flex items-center gap-2 mb-1">
            <span className="w-2 h-2 rounded-full bg-gray-400 animate-pulse flex-shrink-0" />
            <p className="text-sm font-bold text-gray-700">Live Session — date TBC</p>
          </div>
          <p className="text-xs text-gray-500 leading-relaxed">
            The live calibration session date will be announced by your admin. Your ratings are safe.
          </p>
        </div>
      )}

      <button
        onClick={() => navigate('/leader-review')}
        className="w-full bg-[#011132] text-white text-sm font-medium py-3 rounded-xl hover:bg-[#0a2254] transition-colors"
      >
        ← Back to Leader EPR
      </button>
    </div>
  );

  // ════════════════════════════════════════════════════════════════════════════
  // VIEW 1 — Peer List
  // ════════════════════════════════════════════════════════════════════════════

  if (view === 'list') return (
    <div className="max-w-2xl mx-auto px-4 py-8">
      <PageHeader
        onBack={() => navigate('/leader-review')}
        title="Peer Reviews"
        subtitle={session?.name}
      />

      {/* Overall progress */}
      <div className="bg-white border border-gray-200 rounded-2xl p-5 mb-4">
        <div className="flex items-center justify-between mb-3">
          <p className="text-sm font-semibold text-gray-700">Your progress</p>
          <span className="text-sm font-bold text-gray-900">{ratedCount} / {totalPeers} done</span>
        </div>
        <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
          <div
            className={`h-full rounded-full transition-all duration-500 ${allDone ? 'bg-emerald-500' : 'bg-[#011132]'}`}
            style={{ width: `${progressPct}%` }}
          />
        </div>
        {allDone && (
          <p className="text-xs text-emerald-600 font-medium mt-2 flex items-center gap-1">
            <span className="material-symbols-rounded" style={{ fontSize: 14 }}>check_circle</span>
            All peers rated — great work!
          </p>
        )}
      </div>

      {/* Peer cards */}
      {peers.length === 0 ? (
        <div className="bg-white border border-gray-200 rounded-2xl p-8 text-center">
          <span className="material-symbols-rounded text-gray-300 mb-3 block" style={{ fontSize: 40 }}>group_off</span>
          <p className="text-sm font-semibold text-gray-700 mb-1">No peers enrolled yet</p>
          <p className="text-xs text-gray-500 leading-relaxed max-w-xs mx-auto">
            You are the only leader enrolled in this session so far. Peer review will be available once other leaders are enrolled by admin.
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          {peers.map((peer, i) => {
            const done = alreadyRated.has(peer.leaderId);
            return (
              <button
                key={peer.id}
                onClick={() => startReview(i)}
                className={`w-full text-left bg-white border rounded-2xl p-4 transition-all hover:shadow-md active:scale-[0.99] ${done ? 'border-emerald-200 bg-emerald-50/40' : 'border-gray-200 hover:border-[#011132]/30'}`}
              >
                <div className="flex items-center gap-3">
                  <Avatar name={peer.leaderName} size="md" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-semibold text-gray-900 truncate">{peer.leaderName}</p>
                      {done && (
                        <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-emerald-700 bg-emerald-100 px-1.5 py-0.5 rounded-full flex-shrink-0">
                          <span className="material-symbols-rounded" style={{ fontSize: 11 }}>check</span>
                          Done
                        </span>
                      )}
                      {!done && (
                        <span className="text-[10px] font-medium text-amber-700 bg-amber-50 border border-amber-200 px-1.5 py-0.5 rounded-full flex-shrink-0">
                          Pending
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-gray-500 truncate">{peer.leaderJobTitle || 'Leader'}</p>
                    <KRBar pct={peer.krDeliveryPct} />
                  </div>
                  <span className="material-symbols-rounded text-gray-400 flex-shrink-0" style={{ fontSize: 20 }}>
                    {done ? 'edit' : 'chevron_right'}
                  </span>
                </div>
              </button>
            );
          })}
        </div>
      )}

    </div>
  );

  // ════════════════════════════════════════════════════════════════════════════
  // VIEW 2 — Muscle Wizard (one muscle at a time)
  // ════════════════════════════════════════════════════════════════════════════

  if (view === 'review' && currentPeer && currentMuscle) {
    const stepNum  = currentMuscleIdx + 1;
    const stepTotal = muscles.length;
    const peerAlreadyRated = alreadyRated.has(currentPeer.leaderId);

    return (
      <div className="max-w-2xl mx-auto px-4 py-8">
        <PageHeader
          onBack={() => { setView('list'); }}
          title={currentPeer.leaderName}
          subtitle={currentPeer.leaderJobTitle || 'Leader'}
        />

        {/* Peer context strip */}
        <div className="flex items-center gap-3 bg-white border border-gray-200 rounded-2xl px-4 py-3 mb-5">
          <Avatar name={currentPeer.leaderName} size="sm" />
          <div className="flex-1 min-w-0">
            <p className="text-xs font-semibold text-gray-600">KR Delivery</p>
            <KRBar pct={currentPeer.krDeliveryPct} />
          </div>
          {peerAlreadyRated && (
            <span className="text-[10px] font-semibold text-amber-700 bg-amber-50 border border-amber-200 px-2 py-0.5 rounded-full flex-shrink-0">
              Editing
            </span>
          )}
        </div>

        {/* Step indicator — dot row */}
        <div className="flex items-center gap-1.5 mb-5 px-1">
          {muscles.map((m, i) => {
            const s = scores.find(sc => sc.muscleKey === m.key)?.score ?? 0;
            const isCurrent = i === currentMuscleIdx;
            const isDone    = s > 0;
            return (
              <button
                key={m.key}
                onClick={() => { setCurrentMuscleIdx(i); setShowAnchor(false); }}
                title={m.name}
                className={`h-2 rounded-full transition-all flex-1 ${isCurrent ? 'bg-[#011132]' : isDone ? 'bg-[#011132]/40' : 'bg-gray-200'}`}
              />
            );
          })}
        </div>
        <p className="text-xs text-gray-600 text-center font-medium mb-5">
          Muscle <span className="font-bold text-gray-800">{stepNum}</span> of {stepTotal} — {currentMuscle.name}
        </p>

        {/* Muscle card */}
        <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden shadow-sm mb-4">

          {/* Muscle header */}
          <div className="px-5 pt-5 pb-4">
            <div className="flex items-start justify-between gap-3">
              <div className="flex-1">
                <h2 className="text-lg font-bold text-gray-900 mb-0.5">{currentMuscle.name}</h2>
                <p className="text-sm text-gray-600 leading-relaxed">{currentMuscle.shortDesc}</p>
              </div>
              {currentMuscle.goodLooksLike && (
                <button
                  onClick={() => setShowAnchor(p => !p)}
                  title={showAnchor ? 'Hide examples' : 'What does this look like?'}
                  className={`flex-shrink-0 flex items-center gap-1 text-xs font-medium px-2.5 py-1.5 rounded-lg border transition-colors ${showAnchor ? 'bg-[#011132] text-white border-[#011132]' : 'text-blue-600 border-blue-200 bg-blue-50 hover:bg-blue-100'}`}
                >
                  <span className="material-symbols-rounded" style={{ fontSize: 14 }}>
                    {showAnchor ? 'close' : 'info'}
                  </span>
                  {showAnchor ? 'Close' : 'Examples'}
                </button>
              )}
            </div>
          </div>

          {/* Behaviour anchor panel */}
          {showAnchor && currentMuscle.goodLooksLike && (
            <div className="grid grid-cols-2 gap-2 px-5 pb-4">
              <div className="bg-emerald-50 border border-emerald-200 rounded-xl px-3 py-3">
                <p className="text-xs font-bold text-emerald-700 uppercase tracking-wider mb-1.5">Good looks like</p>
                <p className="text-xs text-emerald-900 leading-relaxed whitespace-pre-line">{currentMuscle.goodLooksLike}</p>
              </div>
              <div className="bg-red-50 border border-red-200 rounded-xl px-3 py-3">
                <p className="text-xs font-bold text-red-700 uppercase tracking-wider mb-1.5">Weak looks like</p>
                <p className="text-xs text-red-800 leading-relaxed whitespace-pre-line">{currentMuscle.weakLooksLike}</p>
              </div>
            </div>
          )}

          <div className="border-t border-gray-100 mx-5" />

          {/* Score selector */}
          <div className="px-5 py-5">
            <p className="text-xs font-bold text-gray-600 uppercase tracking-wider mb-3">
              How would you rate <span className="text-gray-800 normal-case font-bold">{currentPeer.leaderName.split(' ')[0]}</span> on this?
            </p>
            <div className="grid grid-cols-5 gap-2">
              {[1, 2, 3, 4, 5].map(n => {
                const active = currentScore === n;
                const col    = SCORE_COLORS[n];
                return (
                  <button
                    key={n}
                    onClick={() => setScore(currentMuscle.key, n)}
                    className={`flex flex-col items-center gap-1.5 px-1 py-3 rounded-xl border-2 transition-all ${active ? `${col.bg} ${col.border} ${col.text} scale-105 shadow-sm` : 'border-gray-200 bg-gray-50 text-gray-600 hover:border-gray-300 hover:bg-gray-100'}`}
                  >
                    <span className={`text-xl font-bold ${active ? col.text : 'text-gray-700'}`}>{n}</span>
                    <span className={`text-[10px] font-semibold leading-tight text-center ${active ? col.text : 'text-gray-500'}`}>
                      {SCORE_LABELS[n]}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Rubric hint for selected score */}
          {currentScore > 0 && currentMuscle.rubric?.[currentScore] && (() => {
            const col = SCORE_COLORS[currentScore];
            return (
              <div className={`mx-5 mb-4 ${col.bg} border ${col.border} rounded-xl px-4 py-3`}>
                <p className={`text-xs ${col.text} leading-relaxed font-medium`}>"{currentMuscle.rubric[currentScore]}"</p>
              </div>
            );
          })()}
        </div>

        {/* Navigation */}
        <div className="flex items-center gap-3">
          <button
            onClick={handlePrevMuscle}
            disabled={currentMuscleIdx === 0}
            className="flex items-center gap-1.5 text-sm font-medium text-gray-500 border border-gray-200 px-4 py-2.5 rounded-xl disabled:opacity-30 hover:bg-gray-50 transition-colors"
          >
            <span className="material-symbols-rounded" style={{ fontSize: 16 }}>arrow_back</span>
            Back
          </button>
          <button
            onClick={handleNextMuscle}
            disabled={currentScore === 0}
            className="flex-1 inline-flex items-center justify-center gap-2 bg-[#011132] text-white text-sm font-semibold py-2.5 rounded-xl disabled:opacity-30 hover:bg-[#0a2254] transition-colors"
          >
            {currentMuscleIdx < muscles.length - 1 ? (
              <>Next Muscle <span className="material-symbols-rounded" style={{ fontSize: 16 }}>arrow_forward</span></>
            ) : (
              <>Review & Submit <span className="material-symbols-rounded" style={{ fontSize: 16 }}>done_all</span></>
            )}
          </button>
        </div>

        {currentScore === 0 && (
          <p className="text-xs text-center text-gray-500 font-medium mt-2">Select a score to continue</p>
        )}
      </div>
    );
  }

  // ════════════════════════════════════════════════════════════════════════════
  // VIEW 3 — Wrap-up: score summary + qualitative + submit
  // ════════════════════════════════════════════════════════════════════════════

  if (view === 'wrapup' && currentPeer) return (
    <div className="max-w-2xl mx-auto px-4 py-8">
      <PageHeader
        onBack={() => { setView('review'); setCurrentMuscleIdx(muscles.length - 1); }}
        title="Review Summary"
        subtitle={`Rating ${currentPeer.leaderName}`}
      />

      {/* Peer identity */}
      <div className="flex items-center gap-3 bg-white border border-gray-200 rounded-2xl px-4 py-3 mb-4">
        <Avatar name={currentPeer.leaderName} size="md" />
        <div>
          <p className="text-sm font-bold text-gray-900">{currentPeer.leaderName}</p>
          <p className="text-xs text-gray-500">{currentPeer.leaderJobTitle || 'Leader'}</p>
        </div>
      </div>

      {/* Score summary grid */}
      <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden mb-4">
        <div className="px-5 py-3 border-b border-gray-100">
          <p className="text-xs font-bold text-gray-600 uppercase tracking-wider">Score Summary</p>
        </div>
        <div className="divide-y divide-gray-50">
          {muscles.map(m => {
            const s = scores.find(sc => sc.muscleKey === m.key)?.score ?? 0;
            const col = s > 0 ? SCORE_COLORS[s] : null;
            return (
              <div key={m.key} className="flex items-center justify-between px-5 py-2.5">
                <div className="flex items-center gap-2 min-w-0">
                  <p className="text-sm font-medium text-gray-800 truncate">{m.name}</p>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  {s > 0 && col ? (
                    <>
                      <span className={`text-xs font-semibold ${col.text}`}>{SCORE_LABELS[s]}</span>
                      <span className={`w-7 h-7 rounded-full border-2 ${col.bg} ${col.border} flex items-center justify-center text-xs font-bold ${col.text}`}>{s}</span>
                    </>
                  ) : (
                    <button
                      onClick={() => { setCurrentMuscleIdx(muscles.indexOf(m)); setView('review'); setShowAnchor(false); }}
                      className="text-xs text-amber-600 font-medium border border-amber-200 bg-amber-50 px-2 py-0.5 rounded-lg"
                    >
                      Missing — tap to rate
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Qualitative fields */}
      <div className="bg-white border border-gray-200 rounded-2xl p-5 mb-4 space-y-4">
        <div>
          <label className="text-xs font-bold text-gray-600 uppercase tracking-wider block mb-1.5">
            One thing they do really well <span className="normal-case font-normal text-gray-500">(optional)</span>
          </label>
          <textarea
            rows={2}
            className="w-full border-2 border-gray-200 rounded-xl px-3 py-2.5 text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#011132]/20 resize-none"
            placeholder="Their biggest strength as a leader…"
            value={strength}
            onChange={e => setStrength(e.target.value)}
          />
        </div>
        <div>
          <label className="text-xs font-bold text-gray-600 uppercase tracking-wider block mb-1.5">
            One area to grow <span className="normal-case font-normal text-gray-500">(optional)</span>
          </label>
          <textarea
            rows={2}
            className="w-full border-2 border-gray-200 rounded-xl px-3 py-2.5 text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#011132]/20 resize-none"
            placeholder="What would make them even more effective…"
            value={growthArea}
            onChange={e => setGrowthArea(e.target.value)}
          />
        </div>
      </div>

      {/* Submit */}
      {!allScored && (
        <div className="flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 mb-3">
          <span className="material-symbols-rounded text-amber-500 flex-shrink-0" style={{ fontSize: 18 }}>warning</span>
          <p className="text-xs text-amber-700">Some muscles are not rated yet. Go back and complete all {muscles.length} to submit.</p>
        </div>
      )}
      <button
        onClick={handleSubmit}
        disabled={!allScored || saving}
        className="w-full bg-[#011132] text-white text-sm font-semibold py-3 rounded-xl disabled:opacity-40 hover:bg-[#0a2254] transition-colors flex items-center justify-center gap-2"
      >
        {saving ? (
          <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> Submitting…</>
        ) : (
          <><span className="material-symbols-rounded" style={{ fontSize: 16 }}>send</span> Submit Review for {currentPeer.leaderName.split(' ')[0]}</>
        )}
      </button>
      <p className="text-xs text-gray-500 text-center mt-2">Your ratings are confidential and will be aggregated with others.</p>
    </div>
  );

  // Fallback
  return (
    <div className="max-w-lg mx-auto px-4 py-16 text-center">
      <p className="text-gray-400 text-sm">Something went wrong. <button onClick={() => navigate('/leader-review')} className="text-[#011132] underline">Go back</button></p>
    </div>
  );
};

export default LeaderReviewPeers;
